import React, { useState, useCallback, useEffect, useRef } from 'react';
import Icon from './Icon';
import Button from './ui/Button';
import { Z } from '../constants/tokens';
import {
  X, ChevronDown, ChevronRight, LayoutDashboard, PenLine, Scissors, Sparkles,
  Shield, HelpCircle, Keyboard, Clock, MapPin, GitBranch, Users, Globe,
  Film, FileText, QrCode, Wand2, FolderOpen, Settings, Activity, Upload,
  Play, Video, Bot, Star, Flag, ListOrdered, Headphones, Download,
  RotateCcw, Copy, Check, Trash2, AlertTriangle, Monitor, BookOpen,
  icons as allLucideIcons,
} from 'lucide-react';

const SECTIONS = [
  {
    id: 'getting-started',
    icon: LayoutDashboard,
    title: 'Getting Started',
    audience: 'All users',
    content: [
      'Welcome to Giving Words to Voices\u2122 — a platform for transcribing, translating, and preserving Holocaust testimonies.',
      'Start on the <LayoutDashboard> Dashboard to see all subjects. Click a subject card to open their workspace.',
      'The top navigation bar shows: <LayoutDashboard> Dashboard, Subject, <PenLine> Editor, <Sparkles> QA, <Scissors> Clips, <Shield> Admin, Export, and Tools.',
      'Your role determines access: Admin has full control, Editor can edit transcripts and marks, Viewer can read only.',
      'Use the <HelpCircle> help button in the top bar to open this guide anytime.',
    ],
  },
  {
    id: 'dashboard',
    icon: LayoutDashboard,
    title: 'Dashboard',
    audience: 'All users',
    content: [
      'The Dashboard shows all subjects as cards. Toggle between <LayoutDashboard> Board view and <ListOrdered> List view.',
      'Click the <Star> star to favorite a subject — favorites appear first.',
      'Each card shows: subject name, tape count, language flags, and progress indicators.',
      'Use the search bar to filter subjects by name.',
      'Admins see additional info: AI cost, session duration, and user activity.',
    ],
  },
  {
    id: 'subject',
    icon: Users,
    title: 'Subject Biography',
    audience: 'Editor, Admin',
    content: [
      'The Subject tab holds all biographical data: name, birth/death dates, places, family, religion, and languages.',
      'Click "Extract from Transcript" to let AI populate fields from the interview transcript.',
      'If data already exists, you get a preview modal to review changes before applying.',
      'The pronunciation field shows how the subject\'s name should be spoken (phonetic).',
      'The <Activity> AI Cost pill shows total API spending for this subject — click to expand the breakdown.',
    ],
  },
  {
    id: 'editor',
    icon: PenLine,
    title: 'Editor',
    audience: 'Editor, Admin',
    content: [
      'The Editor is where you review and correct transcription marks (subtitle segments).',
      'Each mark card shows: timecode, original text, and translation fields for each language.',
      'Click any text field to edit it directly. Changes save automatically.',
      'Use filter pills to narrow marks: My Tasks, All, Flagged, Edited, Assigned, or by category.',
      'The <Flag> Flag button marks a segment for review. The <Trash2> Delete button (admin/editor only) removes a mark.',
      'The blue "EDITED" pill shows edit history on hover — who changed what and when.',
      'Assigned marks show who they\'re assigned to, with an optional note.',
      'The right panel has tabs: Lang Files (VTT management), History (all edits), and Info.',
    ],
  },
  {
    id: 'video',
    icon: Play,
    title: 'Video Playback & Subtitles',
    audience: 'All users',
    content: [
      'The video player appears in the Editor, Clips, and Timeline views.',
      'Use the Subtitles dropdown to select which language\'s VTT subtitles to display.',
      'Keyboard shortcuts: Space = play/pause, Left/Right arrows = seek 5 seconds.',
      'The video syncs with the transcript — clicking a mark jumps to that timecode.',
      'On the Timeline toolbar, the Stamp Times / Clean Locators / AI Review buttons act on the timeline events — not the transcript: matching events to their video timestamps, removing empty locators, and running an AI review/merge pass.',
    ],
  },
  {
    id: 'lang-files',
    icon: Globe,
    title: 'Language Files & VTT Management',
    audience: 'Editor, Admin',
    content: [
      'The Lang Files tab (right panel in Editor) shows all VTT files per language and tape.',
      'Download All Parts: ZIP all VTT files for the current subject.',
      'Write Corrected VTTs: Apply your edits back to the source language VTT files.',
      'Refresh All VTT Files: Regenerate every VTT across all languages from the database. Both operations back up to VTT_BACKUP/.',
      'VTT Versions: Browse and restore previous versions of any VTT file.',
    ],
  },
  {
    id: 'qa',
    icon: Sparkles,
    title: 'QA (Quality Assurance)',
    audience: 'Admin',
    content: [
      'QA uses AI to review transcripts for accuracy, consistency, and completeness.',
      'Run QA stages: Stage 1 (spelling/grammar), Stage 2 (factual consistency), Stage 3 (historical accuracy).',
      'Results show suggested corrections with accept/reject buttons for each.',
      'The QA panel tracks which stages have been completed per tape.',
    ],
  },
  {
    id: 'clips',
    icon: Scissors,
    title: 'Clips',
    audience: 'Editor, Admin',
    content: [
      'Clips extracts meaningful segments from the interview for reuse.',
      'Mark segments in the transcript, then approve or reject clips in the Clips tab.',
      'Approved clips can be dragged into the Assembly — drag to "Essential" or "Queue" sections.',
      'Use the grip handle on assembly clips to reorder them.',
      'Export individual clips or the full assembly as MP4.',
      'The "Non-negotiable Essentials" checklist guides which biographical questions must be covered.',
    ],
  },
  {
    id: 'timeline',
    icon: Clock,
    title: 'Timeline',
    audience: 'Editor, Admin',
    content: [
      'The Timeline visualizes life events chronologically — both from the transcript and from family data.',
      'Click "+ Add Event" to create events manually with date, title, category, and description.',
      'Filter by category pills: Pre-war, Occupation, Camp, Liberation, Post-war, Family.',
      'Pink Family events are auto-generated from the Genogram (birth/death dates of family members).',
      'Click an event in the left list to highlight and scroll to it on the visual timeline, and vice versa.',
      'Events with audio/video references link back to specific tape timecodes.',
    ],
  },
  {
    id: 'places',
    icon: MapPin,
    title: 'Places & Map',
    audience: 'Editor, Admin',
    content: [
      'The Places tab shows all locations mentioned in the testimony on an interactive map.',
      'Each place has: name, type (city/camp/ghetto/etc.), coordinates, and date range.',
      'Click a place on the map to see details and linked transcript references.',
      'Autocomplete suggests Holocaust-relevant places (camps, ghettos, cities) as you type.',
    ],
  },
  {
    id: 'genogram',
    icon: GitBranch,
    title: 'Genogram (Family Tree)',
    audience: 'Editor, Admin',
    content: [
      'The Genogram tab builds a visual family tree from the testimony.',
      'Add family members with name, relationship, birth/death dates, and notes.',
      'Create relationships between members — Partner B can be left blank for single-parent families.',
      'Drag ungrouped members (including the subject) into family groups.',
      'Export as GEDCOM file for use in genealogy software, or backup the full genogram as JSON.',
      'Family birth/death dates automatically appear as pink events on the Timeline.',
    ],
  },
  {
    id: 'prompts',
    icon: BookOpen,
    title: 'Prompt Library & NotebookLM',
    audience: 'Admin',
    content: [
      'The Prompt Library (under Tools) stores AI prompt templates for generating content about the subject.',
      'Three tabs: Prompts (create/edit/run), Generated (view outputs), Styles (writing tone presets).',
      'Use colored placeholder pills to insert dynamic data: {{subject_name}}, {{biography}}, {{timeline}}, etc.',
      'The Style Library has 10 built-in styles (Academic, Documentary, Memorial, etc.) and supports custom styles.',
      'Apply a style to any prompt to set its system prompt / writing tone.',
      'Generated outputs are saved with cost tracking and can be copied or downloaded.',
      'NotebookLM export creates a ZIP of all generated content for upload to Google NotebookLM.',
    ],
  },
  {
    id: 'qr-memorial',
    icon: QrCode,
    title: 'QR Codes & Memorial Pages',
    audience: 'Admin',
    content: [
      'The QR Designer (under Tools) creates gravestone QR codes linking to memorial web pages.',
      'Design the QR code with custom colors, logo, and size. Export as SVG.',
      'The Memorial Page editor builds a public-facing page with bio, photos, and timeline.',
      'Export the memorial as a standalone HTML file that works without any server.',
    ],
  },
  {
    id: 'converter',
    icon: Monitor,
    title: 'MKV Converter',
    audience: 'Admin',
    content: [
      'The MKV → MP4 Converter (under Tools) transcodes video files for web playback.',
      'Browse files on the NAS via the sidebar, or use local file selection in supported browsers.',
      'Click CONVERT on any .mkv file to start transcoding. Multiple jobs run in parallel.',
      'The Conversion Queue shows progress with percentage and allows cancellation.',
      'Converted MP4 files are placed alongside the original MKV.',
    ],
  },
  {
    id: 'admin',
    icon: Shield,
    title: 'Admin Dashboard',
    audience: 'Admin',
    content: [
      'The Admin tab shows system overview: active users, session stats, job queue, and disk usage.',
      'Manage users: create accounts, assign roles (Admin/Editor/Viewer), set working directories.',
      'Help Menus: Create and edit help content that appears in the <HelpCircle> guide (this modal).',
      'System tools: Database cleanup, session management, API key configuration.',
      'All admin sections are collapsed by default — click headers to expand.',
    ],
  },
  {
    id: 'export',
    icon: Download,
    title: 'Export & Downloads',
    audience: 'Editor, Admin',
    content: [
      'The Export menu provides project-level downloads.',
      'Download All Parts: ZIP of all VTT files across languages and tapes.',
      'Project ZIP: Complete archive with video metadata, transcripts, and translations.',
      'NotebookLM ZIP: All generated AI content packaged for Google NotebookLM.',
      'All exported filenames include timestamps to prevent overwrites.',
    ],
  },
  {
    id: 'keyboard',
    icon: Keyboard,
    title: 'Keyboard Shortcuts',
    audience: 'All users',
    content: [
      'Open the shortcuts palette with the <Keyboard> icon in the top bar.',
      'Default shortcuts: Space (play/pause), Shift+E (mark error), Shift+Q (mark quote), Left/Right arrows (seek 5s).',
      'Click any shortcut binding to remap it — press your preferred key combination.',
      'Reset to defaults with the reset button.',
      'The shortcuts palette is draggable — position it anywhere on screen.',
    ],
  },
  {
    id: 'icons',
    icon: HelpCircle,
    title: 'Icons Reference',
    audience: 'All users',
    content: [
      '<LayoutDashboard> Dashboard — main subject overview',
      '<PenLine> Editor — transcript editing workspace',
      '<Scissors> Clips — video clip extraction and assembly',
      '<Sparkles> QA — AI quality assurance review',
      '<Shield> Admin — system administration',
      '<Star> Favorite — pin subjects to top of dashboard',
      '<Flag> Flag — mark a transcript segment for review',
      '<Users> Assigned — segment assigned to a user',
      '<Clock> History — view edit history and changes',
      '<Globe> Languages — VTT files and translation management',
      '<GitBranch> Genogram — family tree builder',
      '<MapPin> Places — locations mentioned in testimony',
      '<Play> Play — video playback controls',
      '<Download> Export — download files and archives',
      '<Settings> Settings — app configuration and preferences',
      '<Keyboard> Shortcuts — keyboard shortcut palette',
      '<HelpCircle> Help — this workflow guide',
      '<Trash2> Delete — remove item (red, admin/editor only)',
      '<RotateCcw> Restore — revert to previous version',
      '<Copy> Copy — copy content to clipboard',
      '<Bot> AI — AI-generated or AI-reviewed content',
      '<AlertTriangle> Warning — needs attention or review',
    ],
  },
];

// Parse <IconName> tags in content strings into actual Lucide icon components
const ICON_MAP = {
  LayoutDashboard, PenLine, Scissors, Sparkles, Shield, HelpCircle, Keyboard,
  Clock, MapPin, GitBranch, Users, Globe, Film, FileText, QrCode, Wand2,
  FolderOpen, Settings, Activity, Upload, Play, Video, Bot, Star, Flag,
  ListOrdered, Headphones, Download, RotateCcw, Copy, Check, Trash2,
  AlertTriangle, Monitor, BookOpen,
};

function RichText({ text, accentColor }) {
  const parts = text.split(/(<[A-Z][A-Za-z0-9]*>)/g);
  return (
    <>
      {parts.map((part, i) => {
        const m = part.match(/^<([A-Z][A-Za-z0-9]*)>$/);
        if (m) {
          const LucideIcon = ICON_MAP[m[1]] || allLucideIcons[m[1]];
          if (LucideIcon) return <span key={i} style={{ display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle', margin: '0 2px' }}><LucideIcon size={13} color={accentColor} /></span>;
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}

export default function WorkflowGuide({ onClose, theme }) {
  const [expandedSections, setExpandedSections] = useState(() => {
    try {
      const saved = sessionStorage.getItem('gwv_workflow_guide_expanded');
      if (saved) return new Set(JSON.parse(saved));
    } catch { /* ignore malformed stored value */ }
    return new Set(['getting-started']);
  });

  useEffect(() => {
    try { sessionStorage.setItem('gwv_workflow_guide_expanded', JSON.stringify([...expandedSections])); } catch { /* ignore quota/serialize errors */ }
  }, [expandedSections]);

  // Modal behaviour: Escape closes (every other modal editor here does),
  // focus moves into the dialog on open so keyboard users are not left on
  // content hidden behind the backdrop, and focus returns to whatever opened
  // it on close. Tab is kept inside the dialog while it is open.
  const dialogRef = useRef(null);
  useEffect(() => {
    const opener = document.activeElement;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown);
    // Focus the dialog itself rather than the close button, so a screen reader
    // announces the guide instead of just "close".
    dialogRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (opener && typeof opener.focus === 'function') opener.focus();
    };
  }, [onClose]);

  const toggleSection = useCallback((id) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => setExpandedSections(new Set(SECTIONS.map(s => s.id))), []);
  const collapseAll = useCallback(() => setExpandedSections(new Set()), []);

  const accentColor = theme.glossaryAccent || '#f59e0b';

  // Audience badge colors
  const audienceColor = useCallback((audience) => {
    if (audience.includes('Admin')) return { bg: 'rgba(239,68,68,0.1)', text: '#ef4444', border: 'rgba(239,68,68,0.25)' };
    if (audience.includes('Editor')) return { bg: 'rgba(59,130,246,0.1)', text: '#3b82f6', border: 'rgba(59,130,246,0.25)' };
    return { bg: theme.card || '#f3f4f6', text: theme.textSub || '#888', border: theme.border || '#e5e7eb' };
  }, [theme]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: Z.modal,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '24px 16px', overflowY: 'auto',
        backgroundColor: 'rgba(0,0,0,0.5)',
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="gwv-workflow-guide-title"
        tabIndex={-1}
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '680px',
          borderRadius: '16px', overflow: 'hidden',
          outline: 'none',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          background: theme.sidebar || '#fff',
          border: `1px solid ${theme.border || '#e5e7eb'}`,
          display: 'flex', flexDirection: 'column',
          maxHeight: 'calc(100vh - 48px)',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: `1px solid ${theme.border || '#e5e7eb'}`,
          flexShrink: 0,
          position: 'sticky', top: 0, zIndex: 10,
          background: theme.sidebar || '#fff',
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon icon={BookOpen} size={18} color={accentColor} />
              <span id="gwv-workflow-guide-title" style={{ fontWeight: 700, fontSize: '1rem', color: theme.text }}>
                Workflow Guide
              </span>
            </div>
            <div style={{ fontSize: '0.7rem', color: theme.textSub, marginTop: '2px', marginLeft: '26px', whiteSpace: 'nowrap' }}>
              How to use Giving Words to Voices™
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button onClick={expandAll} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '0.65rem', color: accentColor, fontWeight: 600,
              padding: '6px 8px',
            }}>
              Expand All
            </button>
            <button onClick={collapseAll} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '0.65rem', color: accentColor, fontWeight: 600,
              padding: '6px 8px',
            }}>
              Collapse All
            </button>
            <Button theme={theme} variant="ghost" size="sm" icon={X} onClick={onClose} aria-label="Close guide" title="Close guide" />
          </div>
        </div>

        {/* Sections */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '12px 16px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {SECTIONS.map(section => {
              const SectionIcon = section.icon;
              const isExpanded = expandedSections.has(section.id);
              const badge = audienceColor(section.audience);
              return (
                <div key={section.id} style={{
                  borderRadius: '10px', overflow: 'hidden',
                  border: `1px solid ${theme.border || '#e5e7eb'}`,
                  background: isExpanded ? (theme.card || '#f9fafb') : 'transparent',
                  transition: 'background 0.15s',
                }}>
                  <button
                    onClick={() => toggleSection(section.id)}
                    aria-expanded={isExpanded}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '10px 14px', textAlign: 'left',
                      background: 'none', border: 'none', cursor: 'pointer',
                      transition: 'opacity 0.15s',
                    }}
                  >
                    <Icon icon={isExpanded ? ChevronDown : ChevronRight} size={13} color={theme.textSub} />
                    <Icon icon={SectionIcon} size={15} color={accentColor} />
                    <span style={{
                      fontSize: '0.82rem', fontWeight: 600, flex: 1,
                      color: theme.text,
                    }}>
                      {section.title}
                    </span>
                    <span style={{
                      fontSize: '0.58rem', padding: '2px 8px',
                      borderRadius: '999px', fontWeight: 600,
                      background: badge.bg, color: badge.text,
                      border: `1px solid ${badge.border}`,
                    }}>
                      {section.audience}
                    </span>
                  </button>
                  {isExpanded && (
                    <div style={{ padding: '0 14px 12px 42px' }}>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {section.content.map((line, i) => (
                          <li key={i} style={{
                            fontSize: '0.75rem', color: theme.textSub,
                            display: 'flex', gap: '8px', lineHeight: 1.5,
                          }}>
                            <span style={{ flexShrink: 0, marginTop: '2px', color: accentColor, fontWeight: 700 }}>-</span>
                            <span><RichText text={line} accentColor={accentColor} /></span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px', textAlign: 'center',
          borderTop: `1px solid ${theme.border || '#e5e7eb'}`,
          flexShrink: 0,
        }}>
          <span style={{ fontSize: '0.7rem', color: theme.textSub }}>
            Questions? Contact Michael Schwarz
          </span>
        </div>
      </div>
    </div>
  );
}
